'use strict';

import { getLoggedServingsOfRecipe, getParticipantsInfo, getParticipantsForSpecificMeal } from '../utils/Meals';
import { flatMap, uniq, uniqBy } from 'lodash';

export function generateOffsets(user, meals, offset, mealType, participants, totalDays) {
    const existingMeal = meals.find(meal => meal.meal === mealType && offset === meal.offset);

    let existingMealLeftovers = [];
    if (existingMeal) {
        existingMealLeftovers = meals.filter((m) => m.parent == existingMeal.id);
    }

    const totalLeftovers = totalDays ? totalDays : existingMealLeftovers.length;

    const leftovers = [];

    let currentOffset = offset;

    for (let i = 0; i < totalLeftovers && leftovers.length < 5; i++) {
        const leftoverOffset = existingMealLeftovers[i] ? existingMealLeftovers[i].offset : currentOffset + 1;
        const leftoverMealType = existingMealLeftovers[i] ? existingMealLeftovers[i].meal : mealType;
        const leftoverParticipants = existingMealLeftovers[i] ? existingMealLeftovers[i].participants : participants;
        const participantDetails  = getParticipantsInfo(user);
        const leftoverParticipantsInfo = participantDetails.filter(p => leftoverParticipants.includes(p.uuid));
        leftovers.push({leftoverOffset, mealType: leftoverMealType, participants: leftoverParticipantsInfo});
    }

    return leftovers;
}

export function getParticipantsForMeal(plan, meal, profile) {

    if (!profile) {
        return null;
    }

    if (!meal.participants) {
        const name = profile.name ? profile.name : profile.first_name;
        //Use portion of 1 because when a meal doesn't have participants it is a food that should not be pre-scaled
        return [{ uuid: 'USER', name, portion: 1 }];
    }

    let guestNo = 1;
    const mealAndLeftovers = getMealAndLeftovers(meal, plan);

    const participantDetails = getParticipantsInfo(profile);
    const participants = uniq(flatMap(mealAndLeftovers, m => m.participants));

    return participants?.map((uuid) => {
        const member = participantDetails.find(p => p.uuid === uuid);

        if (member) {
            return member;
        }

        if (!uuid || uuid === '' || uuid.startsWith('guest-')) {
            return { uuid, name: 'Guest #' + guestNo++, portion: 1 };
        }

        return null;
    }).filter(p => p !== null);
}

export function getMealAndLeftovers(meal, plan) {
    let parent = null;
    let allMeals = [];

    if (meal.meal_type == 'leftover') {
        parent = plan.items.find((item) => item.id === meal.parent);
    } else {
        parent = meal;
    }

    allMeals = plan.items.filter((item) => item.parent === parent.id && !item.deleted);
    allMeals.unshift(parent);

    return allMeals;
}

// Alias for EditMealServingsModalPrimary universal compatibility
export function getMealAndLeftoversFromPlan(meal, plan) {
    return getMealAndLeftovers(meal, plan);
}

/**
 * Retrieves batch information for a given meal, including participants, servings needed and total days.
 *
 * @param {number} offset - The day offset for the meal in the plan.
 * @param {string} mealType - The type of meal to retrieve.
 * @param {Object} profile - The user profile, used to get participants for the meal.
 * @param {Object} plan - The meal plan object which contains all scheduled meals and related information.
 *
 * @returns {Object|null} An object, or null if no meal is found.
 * @returns {Array} return.participants - An array of participants for the meal.
 * @returns {number} return.mealServingsNeeded - Total number of servings needed for the meal.
 * @returns {number} return.totalDays - The total number of days covered by the meal and leftovers.
 */
export function getBatchInfo(offset, mealType, profile, plan) {
    const alreadyScheduledMeal = plan.items.find(
        (m) => m.meal === mealType && m.offset === offset && m.meal_type !== 'leftover' && !m.deleted
    );

    if (alreadyScheduledMeal) {
        const allMeals = getMealAndLeftovers(alreadyScheduledMeal, plan);
        const totalDays = allMeals.length;

        // Sum servings needed across ALL meals (main + leftovers) individually
        // Each meal can have different participants, so we cannot multiply by totalDays
        let mealServingsNeeded = 0;
        allMeals.forEach(meal => {
            const mealParticipants = getParticipantsForSpecificMeal(meal, profile);
            mealServingsNeeded += mealParticipants.reduce((sum, p) => sum + p.portion, 0);
        });

        // Get participants for display (use main meal participants)
        const participants = getParticipantsForMeal(plan, alreadyScheduledMeal, profile);

        return {
            participants,
            mealServingsNeeded,
            totalDays,
        };
    }

    return null;
}